import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { createSliceHook, makeActionCreator } from '@restore/helpers';

import {
  ChangeScoringFormDataAction,
  FormManagementState,
  OnScoringFormRenderProps,
  SaveDraftReadingTaskAction,
  SubmitReadingTaskAction,
  ViewerToolAction,
} from './formManagement.types';

const initialState: FormManagementState = {
  dataToSubmit: null,
  formDataCache: null,
  formRendered: false,
  isDirty: false,
  formIsValid: undefined,
  isReadyToSubmit: false,
  confirmationModalOpened: false,
  lastSeries: null,
  resetDirtyOnNextRender: false,
  readOnly: false,
};

const { act, actEmpty } = makeActionCreator<FormManagementState>();

const propSetter = <K extends keyof FormManagementState>(key: K) =>
  act<FormManagementState[K]>(v => ({ [key]: v }));

// Ensure we never store references that could be mutated by dynamic forms later.
const cloneMutable = (val: unknown) => {
  try {
    const sc = (
      globalThis as unknown as { structuredClone?: (v: unknown) => unknown }
    ).structuredClone;
    if (typeof sc === 'function') {
      return sc(val);
    }
  } catch {
    // ignore
  }
  try {
    return JSON.parse(JSON.stringify(val));
  } catch {
    // ignore
  }
  return _.cloneDeep(val);
};

export const formManagementSlice = createSlice({
  name: 'formManagement',
  initialState,
  reducers: {
    // Form Management actions
    setFormRendered: propSetter('formRendered'),
    setSubmitConfirmationModalOpened: propSetter('confirmationModalOpened'),
    setFormDirty: propSetter('isDirty'),
    setFormReadyToSubmit: propSetter('isReadyToSubmit'),
    setResetDirtyOnNextRender: propSetter('resetDirtyOnNextRender'),
    setReadOnly: propSetter('readOnly'),

    showSignConfirmationModal: act<FormManagementState['dataToSubmit']>(
      dataToSubmit => ({
        dataToSubmit: cloneMutable(dataToSubmit),
        confirmationModalOpened: true,
      })
    ),
    scoringFormRenderHook: actEmpty<OnScoringFormRenderProps>(),
    hideSignConfirmationModal: act(() => ({
      confirmationModalOpened: false,
    })),
    saveDraftReadingTask: actEmpty<SaveDraftReadingTaskAction['payload']>(),
    changeScoringFormData: act<ChangeScoringFormDataAction['payload']>(
      (formDataEvent, state) => {
        let isDirty = state.isDirty;

        // If the form is read-only (task/subtask COMPLETED), never mark as dirty
        if (state.readOnly) {
          isDirty = false;
        } else if (formDataEvent.formData === null) {
          isDirty = false;
        } else if (
          state.formDataCache !== null &&
          JSON.stringify(formDataEvent.formData) !== JSON.stringify(state.formDataCache)
        ) {
          isDirty = true;
        }

        return {
          formDataCache: cloneMutable(formDataEvent.formData ?? null),
          formIsValid: formDataEvent.isValid,
          isDirty,
        };
      }
    ),
    clearLS: actEmpty<void>(),
    submitReadingTask: actEmpty<SubmitReadingTaskAction['payload']>(),

    viewerToolAction: actEmpty<ViewerToolAction['payload']>(),
    setLastSeries: act<FormManagementState['lastSeries']>(lastSeries => ({
      lastSeries,
    })),
  },
  selectors: {
    selectFormManagementState: state => state,
    selectDataToSubmit: state => state.dataToSubmit,
    selectConfirmationModalOpened: state => state.confirmationModalOpened,
    selectFormRendered: state => state.formRendered,
  },
});

export const actions = { ...formManagementSlice.actions };

export const useFormManagementSlice = createSliceHook(
  formManagementSlice.name,
  formManagementSlice.actions
);
